import React, { useState, useMemo } from "react";
import {Alert, Button, Checkbox, Flex} from "antd";
import {MdDoneAll, MdOutlineClear} from "react-icons/md";

export interface BuildingRow {
    buildingName: string;
    roofFaceIds: string[];
    estimatedCount: number;
}

export interface BuildingReferenceMarker {
    label: string;
    xPct: number;
    yPct: number;
}

interface SelectBuildingsForModuleFillProps {
    buildingRows: BuildingRow[];
    total: number;
    referenceImageDataUrl?: string;
    referenceMarkers?: BuildingReferenceMarker[];
    onConfirm: (selectedRoofFaceIds: string[]) => void;
    onCancel: () => void;
}

const SelectBuildingsForModuleFill: React.FC<SelectBuildingsForModuleFillProps> = ({
    buildingRows,
    total,
    referenceImageDataUrl,
    referenceMarkers,
    onConfirm,
    onCancel,
}) => {
    const [selectedBuildingNames, setSelectedBuildingNames] = useState<Set<string>>(() =>
        new Set(buildingRows.map((r) => r.buildingName))
    );
    const [imageZoom, setImageZoom] = useState(1);

    const toggleBuilding = (buildingName: string) => {
        setSelectedBuildingNames((prev) => {
            const next = new Set(prev);
            if (next.has(buildingName)) next.delete(buildingName);
            else next.add(buildingName);
            return next;
        });
    };

    const selectAll = () => setSelectedBuildingNames(new Set(buildingRows.map((r) => r.buildingName)));
    const clearAll = () => setSelectedBuildingNames(new Set());

    const selectedRoofFaceIds = useMemo(() => {
        return buildingRows
            .filter((r) => selectedBuildingNames.has(r.buildingName))
            .flatMap((r) => r.roofFaceIds);
    }, [buildingRows, selectedBuildingNames]);

    const selectedTotal = useMemo(() => {
        return buildingRows
            .filter((r) => selectedBuildingNames.has(r.buildingName))
            .reduce((sum, r) => sum + r.estimatedCount, 0);
    }, [buildingRows, selectedBuildingNames]);

    const handleConfirm = () => {
        onConfirm(selectedRoofFaceIds);
    };

    const handleCancel = () => {
        onCancel();
    };

    const zoomIn = () => setImageZoom((prev) => Math.min(3, +(prev + 0.2).toFixed(2)));
    const zoomOut = () => setImageZoom((prev) => Math.max(0.5, +(prev - 0.2).toFixed(2)));
    const resetZoom = () => setImageZoom(1);

    return (
        <>
            <p className="report-text">
                The loaded design is a very large with multiple primary buildings.
                This design has an estimated maxfill of <strong style={{color:"indianred"}}>{total}</strong> modules across all buildings.
            </p>
            <p className="report-text">
                To keep this design manageable, please select which buildings to fill with modules.
            </p>
        
            <Alert
                style={{ color: "black" }}
                message="Tip : Select buildings with lowest module count to begin with for faster loading, Clicking Cancel or closing this modal will load the design with no modules"
                type="info"
                showIcon
            />
            <div style={{ height: 8 }} />
            <Flex gap={12} align="stretch" style={{ marginBottom: 8 }}>
                {referenceImageDataUrl && (
                    <div style={{ flex: "0 0 44%" }}>
                        <p className="report-text" style={{ marginBottom: 8 }}>
                            Building Number reference
                        </p>
                        <Flex gap={8} style={{ marginBottom: 8 }}>
                            <Button size="small" onClick={zoomOut} disabled={imageZoom <= 0.5}>
                                -
                            </Button>
                            <Button size="small" onClick={zoomIn} disabled={imageZoom >= 3}>
                                +
                            </Button>
                            <Button size="small" onClick={resetZoom} disabled={imageZoom === 1}>
                                Reset
                            </Button>
                            <span style={{ color: "#666", fontSize: 12, display: "flex", alignItems: "center" }}>
                                Zoom: {Math.round(imageZoom * 100)}%
                            </span>
                        </Flex>
                        <div
                            style={{
                                border: "1px solid #d9d9d9",
                                borderRadius: 4,
                                padding: 6,
                                background: "transparent",
                                overflow: "hidden",
                                height: 220,
                            }}
                        >
                            <div
                                style={{
                                    position: "relative",
                                    width: 208,
                                    height: 208,
                                    maxWidth: "100%",
                                    margin: "0 auto",
                                    transform: `scale(${imageZoom})`,
                                    transformOrigin: "center center",
                                }}
                            >
                                <img
                                    src={referenceImageDataUrl}
                                    alt="Top view of buildings for reference"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "fill",
                                        display: "block",
                                        borderRadius: 2,
                                        filter: "brightness(1.22) contrast(1.18) saturate(1.15)",
                                    }}
                                />
                                {(referenceMarkers ?? []).map((marker) => (
                                    <div
                                        key={`${marker.label}-${marker.xPct}-${marker.yPct}`}
                                        style={{
                                            position: "absolute",
                                            left: `${marker.xPct}%`,
                                            top: `${marker.yPct}%`,
                                            transform: "translate(-50%, -50%)",
                                        background: "transparent",
                                        color: "#7bc1ff",
                                        border: "none",
                                        fontSize: 11,
                                            fontWeight: 700,
                                        lineHeight: 1,
                                        textShadow: "0 1px 3px rgba(0,0,0,0.9)",
                                            pointerEvents: "none",
                                        }}
                                    >
                                        {marker.label}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <p className="report-text" style={{ marginTop: 6, marginBottom: 0 }}>
                            Selected: <strong>{selectedTotal}</strong> modules on {selectedRoofFaceIds.length} roof
                            {selectedRoofFaceIds.length !== 1 ? "s" : ""}.
                        </p>
                    </div>
                )}

                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                    <Flex gap={10} style={{ marginBottom: 8 }}>
                        <Button icon={<MdDoneAll />} size="small" onClick={selectAll} style={{ paddingLeft: 0 }}>
                            Select all
                        </Button>
                        <Button icon={<MdOutlineClear />} size="small" onClick={clearAll}>
                            Clear all
                        </Button>
                    </Flex>
                    <div
                        style={{
                            maxHeight: 170,
                            overflowY: "auto",
                            border: "1px solid #d9d9d9",
                            borderRadius: 4,
                            padding: 8,
                            marginBottom: 10,
                        }}
                    >
                        {buildingRows.map((row) => (
                            <div
                                key={row.buildingName}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "6px 0",
                                    borderBottom: "1px solid #f0f0f0",
                                }}
                            >
                                <Checkbox
                                    checked={selectedBuildingNames.has(row.buildingName)}
                                    onChange={() => toggleBuilding(row.buildingName)}
                                >
                                    <span title={row.buildingName}>
                                        Building{" "}
                                        <strong>
                                            {String(row.buildingName).length > 40
                                                ? String(row.buildingName).slice(0, 40) + "..."
                                                : row.buildingName}
                                        </strong>
                                    </span>
                                </Checkbox>
                                <span style={{ color: "#666", marginLeft: 8 }}>
                                    {row.estimatedCount} module{row.estimatedCount !== 1 ? "s" : ""}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className={"modal-bottom-action-button-container"} style={{ marginTop: "auto" }}>
                        <Button
                            type="primary"
                            size="large"
                            className="editor__control--finalize modal-confirm-button"
                            onClick={handleConfirm}
                            disabled={selectedRoofFaceIds.length === 0}
                        >
                            Fill selected buildings
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            className="editor__control--finalize modal-cancel-button"
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </Flex>
        </>
    );
};

export default SelectBuildingsForModuleFill;
